import { LoadingOutlined } from '@ant-design/icons';
import { color, Common } from '@stockbit/ui';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import LoaderBar from 'global/Loading/LoaderBar';
import useStreamConversationModal from 'hooks/useStreamConversationModal';
import ComposeConversation from './ComposeConversation';
import DetailStream from './DetailStream';
import ItemConversation from './ItemConversation';
import ComposeLimited from './ComposeConversation/ComposeLimited';
import {
  ComposeConvoSkeleton,
  LoadConvoButton,
  ModalContainer,
  ModalReply,
  ReplyContainer,
  ReplyContent,
  ReplyListContainer,
  StreamSkeleton,
} from './styled';

import { effects, selectors } from './slice';
import {
  effects as streamEffects,
  selectors as streamSelectors,
} from '../../lib/entities/stream';
import { selectors as profileSelectors } from '../../lib/entities/profile';

const { Text, Flex } = Common;

interface StreamConversationProps {
  onCancel: () => any;
  postId: number | string;
  parentId?: number | string;
  visible: boolean;
  activeCommentId: number | string | null;
  openConversation?: () => void;
  [key: string]: any;
}

function StreamConversation({
  onCancel,
  postId,
  parentId = 0,
  visible,
  activeCommentId,
  openConversation,
  ...props
}: StreamConversationProps) {
  const [repliedComment, setRepliedComment] = useState(null);
  const [hasLoadMore, setLoadMore] = useState(false);
  const [hasAutoLoad, setHasAutoLoad] = useState(false);

  const dispatch = useDispatch();

  const {
    isLoading: isLoadingInitial,
    isLoadingMore,
    selected,
  } = useSelector(selectors);
  const {
    composeConversation: { isLoading: isLoadingCompose },
  } = useSelector(streamSelectors);
  const {
    data: { avatar, username },
  } = useSelector(profileSelectors);

  const scrollToComment = (commentId) => {
    const contentElement = document.getElementById('reply-content-container');
    const commentElement = document.getElementById(`convo-${commentId}`);

    if (commentElement) {
      contentElement.scrollTo({
        top:
          commentElement.offsetTop -
          contentElement.offsetTop -
          commentElement.offsetHeight * 0.1,
      });
    }
  };

  const handleInitialLoad = async () => {
    // @ts-ignore
    const getConvo = await dispatch(
      effects.getStreamConversation({
        postId,
        parentId,
      }),
    );
    // @ts-ignore
    const { data, error } = getConvo.payload;

    if (!error) {
      const moreData = data.prev;
      setLoadMore(moreData);
      const showActiveComment = activeCommentId !== data?.parent?.postid;

      // open conversation modal
      openConversation();

      if (showActiveComment) {
        const commentElement = document.getElementById(
          `convo-${activeCommentId}`,
        );

        if (commentElement) {
          scrollToComment(activeCommentId);
        } else if (data?.replies?.length > 0) {
          // @ts-ignore
          const lastReplyPostId = data?.replies[0]?.postid;
          setHasAutoLoad(true);
          handleLoadMore(lastReplyPostId, true);
        }

        return;
      }

      // If active comment is parent, scroll to last reply
      const contentElement = document.getElementById('reply-content-container');

      if (contentElement) {
        contentElement.scrollTo({ top: contentElement.scrollHeight });
      }
    } else {
      // reset parent state
      onCancel();
    }
  };

  useEffect(() => {
    if (postId) {
      handleInitialLoad();
      // reset convo compose reply box
      // if open another conversation
      setRepliedComment(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, postId]);

  const activeStream: any = selected || {};

  const handleLoadMore = async (
    lastReplyPostId: number,
    autoScroll = false,
  ) => {
    const request = {
      lastReplyPostId,
      postid: postId,
    };
    // @ts-ignore
    const getPrevConvo = await dispatch(
      effects.loadPreviousConversation(request),
    );
    // @ts-ignore
    const { data, error } = getPrevConvo.payload;

    if (!error) {
      setLoadMore(data.more);
      if (autoScroll === true) {
        const commentElement = document.getElementById(
          `convo-${activeCommentId}`,
        );
        if (commentElement) {
          scrollToComment(activeCommentId);
          setHasAutoLoad(false);
        } else if (data.more) {
          // Handling auto load more if convo not found and data.more still true
          await handleLoadMore(data?.replies[0]?.postid, true);
        } else {
          setHasAutoLoad(false);
        }
      } else {
        // Keep current view if user load prev convo manually
        // this code to prevent autoscroll to top of convo
        const commentElement = document.getElementById(
          `convo-${lastReplyPostId}`,
        );
        const contentElement = document.getElementById(
          'reply-content-container',
        );

        contentElement.scrollTo({ top: commentElement.offsetTop });
      }
    }
  };

  const handleReplyComment = (reply) => {
    setRepliedComment(reply);
  };

  const handleComposeConversation = async (val) => {
    const data = {
      ...val,
      parent: activeStream?.parent?.postid,
    };
    // @ts-ignore
    const compose = await dispatch(streamEffects.postConversationStream(data));
    // @ts-ignore
    const { error } = compose.payload;

    if (!error) {
      setRepliedComment(null);
    }

    return !error;
  };

  const handleCancelCompose = () => {
    setRepliedComment(null);
  };

  const isLoading = isLoadingInitial || (isLoadingMore && hasAutoLoad);
  const { is_commentable: isCommentable = false } = activeStream;
  const isAuthorDetailPost = Boolean(activeStream?.parent?.is_author);
  const [detailWidth, detailHeight] = useStreamConversationModal();

  return (
    <ModalReply
      {...props}
      visible={visible}
      closable={false}
      centered
      width="100%"
      mask={false}
      footer={null}
      wrapClassName="modal-conversation"
      bodyStyle={{ height: '100vh', padding: '0' }}
      transitionName=""
      maskTransitionName=""
    >
      <ModalContainer>
        <DetailStream
          post={activeStream?.parent || {}}
          topics={activeStream?.since_message || []}
          onCancel={onCancel}
          isLoading={isLoading}
          handleClose={onCancel}
          dimension={{
            width: detailWidth,
            height: detailHeight,
          }}
        />
        <ReplyContainer>
          <ReplyListContainer id="reply-content-container">
            {isLoading && (
              <ReplyContent>
                <StreamSkeleton />
              </ReplyContent>
            )}

            {!isLoading && (
              <>
                {!hasLoadMore && (
                  <ReplyContent
                    key={`main-post-${activeStream.parent?.postid}`}
                  >
                    <ItemConversation
                      post={activeStream.parent}
                      onComment={handleReplyComment}
                      showCommentLimitInfo={
                        activeStream?.parent?.is_author !== 0
                      }
                      handleClose={onCancel}
                      type="post"
                      isAuthorDetailPost={isAuthorDetailPost}
                    />
                  </ReplyContent>
                )}
                {hasLoadMore && (
                  <Flex
                    direction="column"
                    width="100%"
                    key={`load-more-${activeStream.parent?.postid}`}
                  >
                    <LoadConvoButton>
                      <Text
                        as="a"
                        color={color.primary}
                        onClick={() => {
                          // @ts-ignore
                          const lastReplyPostId = selected?.replies[0]?.postid;
                          handleLoadMore(lastReplyPostId, false);
                        }}
                      >
                        {isLoadingMore && (
                          <LoadingOutlined style={{ marginRight: '12px' }} />
                        )}
                        Load Previous Conversation
                      </Text>
                    </LoadConvoButton>
                    <LoaderBar isLoading={isLoadingMore} />
                  </Flex>
                )}
                {activeStream?.replies?.map((reply) => (
                  <ReplyContent
                    isSelected={Number(activeCommentId) === reply.postid}
                    id={`convo-${reply.postid}`}
                    key={reply.postid}
                  >
                    <ItemConversation
                      post={reply}
                      type="comment"
                      onComment={handleReplyComment}
                      handleClose={onCancel}
                      isAuthorDetailPost={isAuthorDetailPost}
                    />
                  </ReplyContent>
                ))}
              </>
            )}
          </ReplyListContainer>
          {isLoading ? (
            <ComposeConvoSkeleton />
          ) : (
            <>
              {isCommentable ? (
                <ComposeConversation
                  repliedComment={repliedComment}
                  onSubmit={handleComposeConversation}
                  onCancel={handleCancelCompose}
                  isLoading={isLoadingCompose}
                  avatar={avatar}
                  username={username}
                />
              ) : (
                <ComposeLimited />
              )}
            </>
          )}
        </ReplyContainer>
      </ModalContainer>
    </ModalReply>
  );
}

StreamConversation.defaultProps = {
  onCancel: () => {},
  postId: 0,
  parentId: 0,
  visible: false,
  activeCommentId: 0,
  openConversation: () => {},
};

export default StreamConversation;
